import { Link } from "react-router-dom";
import { ArrowUpRight, BellRing, CalendarDays, Car, Plus } from "lucide-react";
import { useFetch } from "@/lib/hooks";
import { da, fmtDate } from "@/lib/format";
import { Empty, Loading, PageHeader, Panel, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";

function Kpi({ label, value, sub, accent, testId }: { label: string; value: string; sub?: string; accent?: boolean; testId: string }) {
  return (
    <div className={`plate relative overflow-hidden rounded-sm p-6 fade-up ${accent ? "border-primary/50" : ""}`} data-testid={testId}>
      {accent && <span className="absolute left-0 top-0 h-full w-[3px] bg-primary" />}
      <div className="eyebrow">{label}</div>
      <div className="mt-3 font-display text-3xl font-bold tracking-tight" data-testid={`${testId}-value`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function ClientResponses({ items }: { items: any[] }) {
  if (!items.length) return null;
  return (
    <div className="mt-6 rounded-sm border border-primary/50 bg-primary/5 p-4 fade-up" data-testid="client-responses-panel">
      <div className="mb-3 flex items-center gap-2 eyebrow"><BellRing className="h-4 w-4 text-primary" />Réponses clients par WhatsApp ({items.length})</div>
      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        {items.map((q) => (
          <Link key={q.id} to={`/devis/${q.id}`} className="flex items-center gap-3 rounded-sm border border-border bg-background/60 p-3 transition-colors hover:border-primary/60" data-testid={`client-response-${q.id}`}>
            <StatusBadge status={q.status} />
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-mono-data font-semibold">{q.number} · {q.plate}</div>
              <div className="truncate text-xs text-muted-foreground">{q.decision?.by_name} · {fmtDate(q.decision?.at)}</div>
            </div>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { data, loading } = useFetch<any>("/dashboard");
  if (loading || !data) return <Loading />;
  const month = new Date().toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  return (
    <div>
      <PageHeader eyebrow={`Tableau de bord · ${month}`} title="Atelier en direct"
        actions={<Link to="/receptions/nouvelle"><Button className="h-11" data-testid="dashboard-new-reception-btn"><Plus className="mr-2 h-4 w-4" />Nouvelle réception</Button></Link>} />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {data.show_money && <Kpi testId="kpi-revenue" label="Chiffre d'affaires HT (mois)" value={da(data.ca_ht)} sub={`TTC ${da(data.ca_ttc)} · avoirs déduits`} accent />}
        {data.show_money && <Kpi testId="kpi-cash" label="Encaissé (mois)" value={da(data.cash_in)} />}
        {data.show_money && <Kpi testId="kpi-stamps" label="Timbres fiscaux (mois)" value={da(data.stamps_month || 0)} sub={`${data.stamps_count || 0} paiement(s) en espèces · à déclarer`} />}
        <Kpi testId="kpi-present" label="Véhicules à l'atelier" value={String(data.present.length)} sub="réceptionnés, non restitués" />
        {data.show_money && <Kpi testId="kpi-unpaid" label="Factures impayées" value={String(data.unpaid.length)} sub={`Reste dû ${da(data.unpaid_total)}`} />}
      </div>
      <ClientResponses items={data.client_responses || []} />
      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <Panel title="Véhicules présents" className="xl:col-span-2" testId="present-vehicles-panel">
          {data.present.length === 0 ? <Empty text="Aucun véhicule à l'atelier" /> : (
            <div className="divide-y divide-border">
              {data.present.map((r: any) => (
                <Link key={r.id} to={`/receptions/${r.id}`} className="flex items-center gap-4 py-3 transition-colors hover:bg-accent/50" data-testid={`present-${r.id}`}>
                  <Car className="h-5 w-5 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono-data font-semibold">{r.plate}</div>
                    <div className="truncate text-xs text-muted-foreground">{r.vehicle_label} · {r.client_name} · entré {fmtDate(r.created_at)}</div>
                  </div>
                  {r.order ? <StatusBadge status={r.order.status} /> : <span className="text-xs text-muted-foreground">Diagnostic / devis</span>}
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          )}
        </Panel>
        <Panel title="Rendez-vous à venir" testId="appointments-panel" actions={<Link to="/agenda" className="text-xs text-muted-foreground hover:text-foreground">Agenda</Link>}>
          {data.appointments.length === 0 ? <Empty text="Aucun rendez-vous" /> : data.appointments.map((a: any) => (
            <div key={a.id} className="flex gap-3 border-b border-border py-3 last:border-0">
              <CalendarDays className="mt-0.5 h-4 w-4 text-sky-400" />
              <div className="text-sm">
                <div className="font-semibold">{fmtDate(a.start)} · <span className="font-mono-data">{a.plate}</span></div>
                <div className="text-xs text-muted-foreground">{a.client_name} — {a.reason}</div>
              </div>
            </div>
          ))}
        </Panel>
        {data.show_money && (
          <Panel title="Impayés" className="xl:col-span-3" testId="unpaid-panel">
            {data.unpaid.length === 0 ? <Empty text="Aucune facture impayée" /> : (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {data.unpaid.map((i: any) => (
                  <Link key={i.id} to={`/factures/${i.id}`} className="rounded-sm border border-border p-4 transition-colors hover:border-primary/60" data-testid={`unpaid-${i.id}`}>
                    <div className="flex justify-between"><span className="font-mono-data font-semibold">{i.number}</span><StatusBadge status={i.payment_status} /></div>
                    <div className="mt-2 text-xs text-muted-foreground">{i.client_name} · {i.plate}</div>
                    <div className="mt-1 font-display text-xl font-bold text-red-300 [.light_&]:text-red-700">{da(i.balance)}</div>
                  </Link>
                ))}
              </div>
            )}
          </Panel>
        )}
      </div>
    </div>
  );
}
